import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import giftImage from '../../../../../common/assets/images/boundle-products/gift-double.png';
import useBreakpoints from '../../../../../common/hooks/useBreakpoints';
import Button from '../../../../../common/widgets/Button';
import Carousel from '../../../../../common/widgets/Carousel';
import { ProductTypes } from '../../../enums';
import { rewriteImageUrl } from '../../../helpers';
import {
  BundleCashBackModel,
  BundleDiscountCodeProductModel,
  BundleGiftCardModel,
  BundleProductModel,
} from '../../../models/Product';
import { resources } from '../../../resources/resources';
import { productsService } from '../../../services/products';
import { showSuccessToast } from '../../../../../common/global/helpers';

interface Props {
  bundleProducts: BundleProductModel[];
}

const isCashbackProduct = (
  product: BundleProductModel,
): product is BundleCashBackModel =>
  product.productTypeId === ProductTypes.CashBack;

const isDiscountCodeProduct = (
  product: BundleProductModel,
): product is BundleDiscountCodeProductModel =>
  product.productTypeId === ProductTypes.FreeLinkVoucher;

const isGiftCardProduct = (
  product: BundleProductModel,
): product is BundleGiftCardModel =>
  product.productTypeId === ProductTypes.DefaultProduct;

// with fractional slidesToShow the last position is fractional (e.g. 0.6),
// so react-slick never marks a dot as slick-active; we mark it ourselves
const renderDot = (activeDot: number) =>
  function renderDotButton(index: number) {
    return (
      <button
        type="button"
        className={index === activeDot ? 'is-active' : undefined}
      />
    );
  };

export default function BundleProductsCarousel({
  bundleProducts,
}: Readonly<Props>) {
  const { t: translate } = useTranslation();
  const { isMobile, isOnlyTablet } = useBreakpoints();
  const navigate = useNavigate();

  const [copiedProductId, setCopiedProductId] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoadingCashback, setIsLoadingCashback] = useState(false);

  if (!bundleProducts.length) return null;

  const showIntroSlide = !isMobile && bundleProducts.length === 1;
  const totalSlides = bundleProducts.length + Number(showIntroSlide);
  const showDots = (isMobile || isOnlyTablet) && totalSlides > 1;
  const showArrows = !showDots && totalSlides > 2;
  const maxSlide = totalSlides - 2;
  const activeDot = Math.ceil(currentSlide);

  const copyCode = async (product: BundleDiscountCodeProductModel) => {
    if (!product.voucherCode) return;

    await navigator.clipboard.writeText(product.voucherCode);
    setCopiedProductId(product.productId);
    showSuccessToast(translate(resources.clipboardCopied));

    globalThis.setTimeout(() => {
      setCopiedProductId(null);
    }, 1500);
  };

  const openCashback = async (product: BundleCashBackModel) => {
    setIsLoadingCashback(true);

    const result = await productsService.getCashBackLink(product.productId);

    if (!result.hasErrors && result.data) {
      window.open(result.data, '_blank', 'noopener,noreferrer');
    }

    setIsLoadingCashback(false);
  };

  const openGiftCardBrand = (product: BundleGiftCardModel) => {
    navigate(
      `/products/search?brand=${product.brandId}_${encodeURIComponent(
        product.brandName,
      )}`,
    );
  };

  const renderIntroSlide = () => (
    <div key="intro" className="bundle-products__slide">
      <div className="bundle-products__intro">
        <div className="bundle-products__gift">
          <img src={giftImage} alt="Gift" />
        </div>

        <div className="bundle-products__text">
          <div className="bundle-products__title">
            {translate(resources.bundleProducts.carousel.title)}
          </div>

          <div className="bundle-products__subtitle">
            {translate(resources.bundleProducts.carousel.subtitle)}
          </div>
        </div>
      </div>
    </div>
  );

  const renderProductSlide = (product: BundleProductModel) => {
    const isCashback = isCashbackProduct(product);
    const isDiscountCode = isDiscountCodeProduct(product);
    const isGiftCard = isGiftCardProduct(product);

    let actionLabel = resources.bundleProducts.carousel.copyCode;

    if (isCashback) {
      actionLabel = resources.bundleProducts.carousel.shopWithCashback;
    } else if (isGiftCard) {
      actionLabel = resources.bundleProducts.carousel.shopNow;
    }

    return (
      <div
        className="bundle-products__slide"
        key={`${product.productTypeId}-${product.productId}`}
      >
        <div className="bundle-product-card">
          <img
            className="bundle-product-card__image"
            src={rewriteImageUrl(product.imagePath ?? '')}
            alt={product.name}
          />

          <div className="bundle-product-card__content">
            {isGiftCard
              ? translate(resources.bundleProducts.carousel.giftCardName, {
                  brandName: product.brandName,
                })
              : product.name}

            {isCashback && (
              <div className="bundle-product-card__discount">
                <strong>
                  {product.cashBackValue}{' '}
                  {translate(resources.bundleProducts.carousel.cashbackSuffix)}
                </strong>
              </div>
            )}

            {isDiscountCode && (
              <div className="bundle-product-card__code">
                {translate(resources.bundleProducts.carousel.discountCodeLabel)}{' '}
                <strong>{product.voucherCode}</strong>
              </div>
            )}
          </div>

          <div className="bundle-product-card__action">
            <Button
              className="btn btn-outline"
              onClick={() => {
                if (isCashback) {
                  openCashback(product);
                  return;
                }

                if (isGiftCard) {
                  openGiftCardBrand(product);
                  return;
                }

                copyCode(product);
              }}
              loading={isCashback ? isLoadingCashback : false}
            >
              {translate(actionLabel)}
            </Button>

            {isDiscountCode && copiedProductId === product.productId && (
              <span className="bundle-product-card__tooltip">
                {translate(resources.bundleProducts.carousel.codeCopiedTooltip)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const carouselSlides = [
    ...(showIntroSlide ? [renderIntroSlide()] : []),
    ...bundleProducts.map(renderProductSlide),
  ];

  const sectionClassName = [
    'bundle-products',
    currentSlide === 0 ? 'bundle-products--start' : '',
    currentSlide >= maxSlide ? 'bundle-products--end' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={sectionClassName}>
      {isMobile && (
        <div className="bundle-products__mobile-intro">
          <div className="bundle-products__title">
            {translate(resources.bundleProducts.carousel.title)}
          </div>

          <div className="bundle-products__subtitle">
            {translate(resources.bundleProducts.carousel.subtitle)}
          </div>
        </div>
      )}

      <Carousel
        loading={false}
        dots={showDots}
        arrows={showArrows}
        infinite={false}
        slidesToShow={2}
        slidesToScroll={1}
        swipe
        draggable
        touchMove
        beforeChange={(_, next) => setCurrentSlide(next)}
        customPaging={renderDot(activeDot)}
        responsive={[
          {
            breakpoint: 1025,
            settings: {
              slidesToShow: 1.4,
              slidesToScroll: 1,
              arrows: false,
              dots: true,
              swipe: true,
              draggable: true,
              touchMove: true,
            },
          },
          {
            breakpoint: 481,
            settings: {
              slidesToShow: 1,
              slidesToScroll: 1,
              arrows: false,
              dots: showDots,
              swipe: true,
              draggable: true,
              touchMove: true,
            },
          },
        ]}
        className="bundle-products__carousel"
      >
        {carouselSlides}
      </Carousel>
    </section>
  );
}
